import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { BBWTMessageService } from '../../services';
import { UserService } from '../../services';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ISupplier } from '../../interfaces/supplier';
import { HaversineDistance } from '../../utils/haversine-distance';
import { RouteRecommendationService } from '../../services/route-recommendation.service';
import { IRouteRecommendationUserPreferenceChangeLimitDetails } from '../../interfaces/route-recommendations/route-recommendation-user-preference-change-limit-detail';
import {
  IPublicTransportStationDTO,
  ISupplierRouteStationPreferenceDTO,
  ISupplierRouteTravelPreferenceDTO,
  ISupplierTravelPreferenceDTO,
} from '../../interfaces/supplier-travel-preference';
import { SupplierTravelPreferencesService } from '../../services/supplier-travel-preferences.service';

@Component({
  selector: 'algo-travel-preferences',
  templateUrl: 'algo-travel-preferences.component.html',
  styleUrls: ['algo-travel-preferences.component.scss'],
})
export class AlgoTravelPreferencesComponent implements OnInit {
  @Input() isInDialog: boolean = false;
  @Input() set visible(visible: boolean) {
    if (visible && this.prefsForm) {
      this.loadPreferences().then(() => {});
    }
  }

  orgType: number;
  hourOptions: { label: string; value: number }[] = [];
  minuteOptions: { label: string; value: number }[] = [];
  cycleDistanceOptions: { label: string; value: number }[] = [];
  contingencyOptions: { label: string; value: number }[] = [];

  sliderTicks: number[] = Array.from({ length: 9 }, (_, i) => i);

  preferredStations: ISupplierRouteStationPreferenceDTO[] = [];
  prefsForm: FormGroup;
  algoTravelPreferences: ISupplierTravelPreferenceDTO;
  showSuggestions: boolean = false;
  suggestions: IPublicTransportStationDTO[] = [];
  firstRun = true;
  saving = false;
  currentOrg: ISupplier = null;
  loadingSupplierStations: boolean;
  dailyChangeLimitDetails: IRouteRecommendationUserPreferenceChangeLimitDetails = null;

  @Output() externalReload: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private router: Router,
    private travelPreferencesService: SupplierTravelPreferencesService,
    private haversineDistance: HaversineDistance,
    public messageService: BBWTMessageService,
    public userService: UserService,
  ) {
    this.hourOptions = Array.from({ length: 5 }, (v, k) => k + 5).map((i) => {
      return { label: i.toString(), value: i };
    });

    // Every 15 minutes
    this.minuteOptions = Array.from({ length: 60 }, (v, k) => k)
      .filter((i) => i % 15 === 0)
      .map((i) => {
        // Use 2 digits
        return { label: i.toString().padStart(2, '0'), value: i };
      });

    this.cycleDistanceOptions = [
      { label: 'Not Set', value: 0 },
      { label: '5 miles', value: 5 },
      { label: '10 miles', value: 10 },
      { label: '15 miles', value: 15 },
      { label: '20 miles', value: 20 },
      { label: '25 miles', value: 25 },
      { label: '30 miles', value: 30 },
      { label: '35 miles', value: 35 },
      { label: '40 miles', value: 40 },
      { label: '45 miles', value: 45 },
      { label: '50 miles', value: 50 },
    ];

    this.contingencyOptions = [
      { label: '- 20 minutes', value: -20 },
      { label: '- 10 minutes', value: -10 },
      { label: '0 minutes', value: 0 },
      { label: '+ 10 minutes', value: 10 },
      { label: '+ 20 minutes (default)', value: 20 },
      { label: '+ 30 minutes', value: 30 },
      { label: '+ 45 minutes', value: 45 },
      { label: '+ 1 hour', value: 60 },
    ];
  }

  typedSupplierTravelStation(
    value: ISupplierRouteStationPreferenceDTO,
  ): IPublicTransportStationDTO | null {
    return value?.station ?? null;
  }

  ngOnInit() {
    this.initForm();

    this.userService.getCurrentUserOrg().then((res) => {
      this.orgType = res != null ? (this.orgType = res.organisationTypeId) : 0;
      this.currentOrg = res;

      this.initStations().then(() => {
        this.loadPreferences().then(() => {});
      });
    });

    this.getDailyChangeLimitDetails();
  }

  async initStations() {
    this.loadingSupplierStations = true;

    return this.travelPreferencesService
      .getAlgoTravelStations({})
      .then((res: IPublicTransportStationDTO[]) => {
        this.suggestions = res;

        this.suggestions.forEach((station) => {
          station.distance = this.haversineDistance.distanceMiles(
            station.latitude,
            station.longitude,
            this.currentOrg.latitude,
            this.currentOrg.longitude,
          );
        });
      });
  }

  loadPreferences() {
    this.loadingSupplierStations = true;
    return this.travelPreferencesService
      .getAlgoTravelPreferences()
      .then((res: ISupplierTravelPreferenceDTO) => {
        this.algoTravelPreferences = res;
        const prefs = (res?.supplierRouteStationPreferences ||
          []) as ISupplierRouteStationPreferenceDTO[];
        this.preferredStations = [...prefs];
        this.preferredStations.forEach((pref) => {
          pref.station = this.suggestions.find((s) => s.id == pref.stationId);
        });
        this.updateForm(res);
      })
      .finally(() => {
        this.loadingSupplierStations = false;
      });
  }

  initForm() {
    this.prefsForm = new FormGroup({
      startTimeHour: new FormControl(),
      startTimeMinute: new FormControl(),
      // Default to balanced: 0.5 (0 = cheapest, 1 = fastest)
      timeCostPriority: new FormControl(0.5),
      hasBike: new FormControl(),
      bikeMiles: new FormControl(),
      station: new FormControl(),
      publicTransportContingency: new FormControl(20),
    });

    this.prefsForm
      .get('station')
      .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(() => {
        this.getStationRecommendations();
      });
  }

  private updateForm(supplierTravelPreference: ISupplierTravelPreferenceDTO) {
    const trp = (supplierTravelPreference as any)
      ?.supplierRouteTravelPreference as ISupplierRouteTravelPreferenceDTO;
    const startTime = trp?.earliestStartTime?.split(':') ?? ['06', '00'];

    this.prefsForm.patchValue({
      startTimeHour: parseInt(startTime[0], 10),
      startTimeMinute: parseInt(startTime[1], 10),
      // timeCostPriority is now a float between 0 and 1
      timeCostPriority: trp?.timeCostPriority ?? 0.5,
      hasBike: trp?.hasBike,
      bikeMiles: trp?.bikeMiles ?? 10,
      publicTransportContingency: trp?.publicTransportContingency ?? 20,
    });
  }

  focusStart() {
    this.showSuggestions = true;

    if (this.firstRun == true) {
      this.firstRun = false;
      this.getStationRecommendations();
    }
  }

  get f() {
    return this.prefsForm.value;
  }

  get timeCostValue(): number {
    return this.prefsForm?.get('timeCostPriority')?.value ?? 0.5;
  }

  get isExtremeCheapest(): boolean {
    // Warn on the cheapest stop of the 9-stop slider
    return this.timeCostValue <= 0.1;
  }

  get isExtremeFastest(): boolean {
    // Warn on the fastest stop of the 9-stop slider
    return this.timeCostValue >= 0.9;
  }

  get showExtremeWarning(): boolean {
    return this.isExtremeCheapest || this.isExtremeFastest;
  }

  get extremeWarningTitle(): string {
    if (this.isExtremeCheapest) return 'Cheapest selected — fewer routes may appear';
    if (this.isExtremeFastest) return 'Fastest selected — fewer routes may appear';
    return '';
  }

  get extremeWarningBody(): string {
    if (this.isExtremeCheapest)
      return 'At the cheapest setting, Juno will always choose the lowest cost option despite the journey duration. Hence it will tend to favour buses wherever possible so routes that genuinely need a taxi to work within time constraints may no longer appear in your suggestions.';

    if (this.isExtremeFastest)
      return 'At the fastest setting, Juno will always choose quickest transport despite the cost. Hence it will favour taxis wherever possible. This higher cost may push some routes below our earnings thresholds.';

    return '';
  }

  private getStationRecommendations() {
    const search = this.prefsForm.get('station').value;
  }

  saveChanges() {
    this.saving = true;

    const earliestStartTime = this.toTimeString(
      this.prefsForm.value.startTimeHour,
      this.prefsForm.value.startTimeMinute,
    );

    const mappedStations = (this.preferredStations || []).map((s) => ({
      supplierId: 0,
      stationId:
        s.stationId != null
          ? String(s.stationId)
          : s.station?.id != null
          ? String(s.station.id as any)
          : null,
      station: null,
      driveToStation: !!s.driveToStation,
    })) as ISupplierRouteStationPreferenceDTO[];

    const dto: ISupplierTravelPreferenceDTO = {
      supplierRouteTravelPreference: {
        supplierId: 0,
        earliestStartTime: earliestStartTime,
        timeCostPriority: this.prefsForm.value.timeCostPriority,
        publicTransportContingency: this.prefsForm.value.publicTransportContingency ?? 20,
        hasBike: !!this.prefsForm.value.hasBike,
        bikeMiles: this.prefsForm.value.bikeMiles ?? 0,
      },
      supplierRouteStationPreferences: mappedStations.length > 0 ? mappedStations : null,
    };

    this.travelPreferencesService.putSupplierTravelPreferences(dto).finally(() => {
      this.saving = false;
      this.externalReload.emit(true);
      this.getDailyChangeLimitDetails();
    });
  }

  addToList(station: IPublicTransportStationDTO) {
    if (this.preferredStations?.length >= 3) return;

    this.preferredStations.push({
      station: station,
      stationId: station.id,
      driveToStation: false,
      supplierId: 0,
      distance: station.distance,
    });
  }

  removeFromList(station: IPublicTransportStationDTO) {
    this.preferredStations = this.preferredStations.filter((s) => s.stationId != station.id);
  }

  filteredSuggestions(
    search: string,
    suggestions: IPublicTransportStationDTO[],
    stations: ISupplierRouteStationPreferenceDTO[],
  ) {
    if (search?.length > 0) {
      return suggestions
        .filter((suggestion) => {
          return (
            !stations.some((station) => station.stationId == suggestion.id) &&
            suggestion.stationName.toLowerCase().includes(search.toLowerCase())
          );
        })
        .slice(0, 100);
    } else {
      return suggestions
        .filter((suggestion) => {
          return !stations.some((station) => station.stationId == suggestion.id);
        })
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 100);
    }
  }

  toggleStation(publicTransportStationId: number | string, toStation: string) {
    const pref = this.preferredStations.find(
      (station) => station.stationId == publicTransportStationId,
    );
    if (!pref) return;
    if (toStation === 'toStationDrive') {
      pref.driveToStation = !pref.driveToStation;
    }
  }

  stationStatus(publicTransportStationId: number | string, toStation: string) {
    const pref = this.preferredStations.find(
      (station) => station.stationId == publicTransportStationId,
    );
    if (!pref) return false;
    if (toStation === 'toStationDrive') {
      return !!pref.driveToStation;
    }
    return false;
  }

  toPlannedRoutesPage() {
    this.router.navigate(['app/routes-inbox'], {});
  }

  getDailyChangeLimitDetails(): void {
    this.travelPreferencesService.getUserPreferenceChangeLimitDetails().then((response) => {
      this.dailyChangeLimitDetails = response;
    });
  }

  private toTimeString(h: number, m: number): string {
    const hh = (h ?? 0).toString().padStart(2, '0');
    const mm = (m ?? 0).toString().padStart(2, '0');
    return `${hh}:${mm}:00`;
  }
}
